// src/pages/admin/NewsListPage.tsx
// ============================================================
// SALT — Admin News List Page
//
// Displays all news posts with status filter (all/published/draft),
// search, pagination. Click row → editor. Create button at top.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';

import '../../styles/admin-news.css';

interface NewsPost {
  post_id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  is_published: boolean;
  published_at: string | null;
  author_username: string;
  categories: string[];
  created_at: string;
  updated_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export default function NewsListPage() {
  const navigate = useNavigate();

  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string | number> = { page, limit: 20, status: statusFilter };
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await axiosClient.get('/admin/news', { params });
      setPosts(res.data.data.posts);
      setPagination(res.data.data.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load news posts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="sl-admin-news">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">News</h1>
        <button
          className="sl-news-create-btn"
          onClick={() => navigate('/admin/news/new')}
        >
          + New Post
        </button>
      </div>

      {/* Filters */}
      <div className="sl-news-filters">
        <div className="sl-news-status-tabs">
          {(['all', 'published', 'draft'] as const).map((s) => (
            <button
              key={s}
              className={`sl-news-status-tab ${statusFilter === s ? 'sl-news-status-tab--active' : ''}`}
              onClick={() => { setStatusFilter(s); setPage(1); }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="sl-news-search"
          placeholder="Search posts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="sl-admin-error">{error}</div>}

      {/* Posts List */}
      <div className="sl-news-list">
        {loading && posts.length === 0 ? (
          <div className="sl-admin-loading">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="sl-admin-empty">No posts found</div>
        ) : (
          posts.map((post) => (
            <div
              key={post.post_id}
              className="sl-news-card"
              onClick={() => navigate(`/admin/news/${post.post_id}`)}
            >
              <div className="sl-news-card-header">
                <h3 className="sl-news-card-title">{post.title}</h3>
                <span className={`sl-news-status ${post.is_published ? 'sl-news-status--published' : 'sl-news-status--draft'}`}>
                  {post.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              {post.excerpt && (
                <p className="sl-news-card-excerpt">{post.excerpt}</p>
              )}
              <div className="sl-news-card-meta">
                <span>by {post.author_username}</span>
                <span>•</span>
                <span>{post.is_published ? formatDate(post.published_at) : `Created ${formatDate(post.created_at)}`}</span>
                {post.categories.length > 0 && (
                  <>
                    <span>•</span>
                    <span className="sl-news-card-categories">
                      {post.categories.join(', ')}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="sl-news-pagination">
          <button
            className="sl-news-pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>
          <span className="sl-news-pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="sl-news-pagination-btn"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
